import { customInstance } from "@/api/client";
import { API_BASE_URL } from "@/api/client";
import { publicFetch } from "@/api/public-fetch";

/**
 * The two API surfaces the consent flow touches from `/me`, and the reason they are
 * reached differently.
 *
 * `/me/partners/:id/consent-envelope` is client-JWT authed and goes through
 * `customInstance`, which attaches the `/me` session token.
 *
 * `/esign/:token/*` is deliberately OUTSIDE application authentication
 * (`routers/esign_signer.py`): the opaque link token is the first credential and the
 * emailed one-time code is the second factor, and a signer is not a platform user. So
 * those calls go through `publicFetch`, which sends no Authorization header, and they set
 * `credentials: "include"` so the signer session cookie the API mints is stored and
 * returned.
 *
 * CROSS-ORIGIN NOTE, because this file is where the mistake would be made. These
 * `/esign/*` calls are the same request shape the signer app already makes: same origin
 * (`/me/*` and `/esign/:token` are one SPA — `main.tsx`), same API origin, same
 * `credentials: "include"`, same cookie path (`/esign/{token}`). Nothing here introduces
 * a new cross-site request shape or a new cookie. What DID change is elapsed time: the
 * cookie is now set several steps before it is used, bounded by the same one hour it
 * always had. Never drop `credentials: "include"` from one of these — without it the API
 * cannot see the session and answers "Envelope not found", which reads as a broken
 * envelope rather than a missing cookie.
 */

const ESIGN_API_BASE_URL =
  API_BASE_URL || (import.meta.env.DEV ? "http://localhost:8081" : "");

export interface ConsentEnvelopeResponse {
  signed: boolean;
  signing_url: string | null;
  return_url: string;
  /** Plaintext link token — present only when this call minted an envelope. */
  link_token: string | null;
  /** Status of a verified-but-unsigned envelope this signer already holds, if any. */
  pending_status: string | null;
  /**
   * The status of the token we said we were carrying — non-null ONLY when the server
   * confirms it still belongs to this run (same signer, same client group, same contact
   * identity). This is the answer the browser cannot work out for itself: a token from
   * another practice, or one bound to an address since changed, resolves and reports
   * `email_verified` just the same.
   */
  carried_token_status: string | null;
}

export interface ConsentEnvelopeRequest {
  return_to?: string;
  force_new?: boolean;
  carried_token?: string;
}

/**
 * Why the early-verification step gave up. A closed set, matching the server's.
 *
 * Kept narrow on purpose: this is telemetry about a flow handling an email address and a
 * bearer token, and a reporting channel becomes a liability the moment it forwards a raw
 * error. Never send a token, an address, or a caught exception through here.
 */
export type VerificationSkipCause =
  | "setup_failed"
  | "verification_failed"
  | "no_link_token";

interface ApiResponse<T> {
  data: T;
}

/** Ask the API for a signer link, or for what it knows about one already issued. */
export async function requestConsentEnvelope(
  partnerId: string,
  body: ConsentEnvelopeRequest = {},
): Promise<ConsentEnvelopeResponse> {
  const response = await customInstance<ApiResponse<ConsentEnvelopeResponse>>(
    `/me/partners/${partnerId}/consent-envelope`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return response.data;
}

/**
 * Tell the server the early-verification step was skipped, so the skipping can be counted.
 *
 * Deliberately fire-and-forget: this reports a degradation, and a degradation report that
 * can itself block the client would be a second way for the optimisation to become the
 * gate it must never be. A failure to record is worse than not recording — it is
 * unrecorded AND stuck — so the call is awaited by nobody and its rejection is swallowed.
 */
export function reportVerificationSkipped(
  partnerId: string,
  cause: VerificationSkipCause,
  statusCode?: number,
): void {
  if (!partnerId) return;
  void customInstance(`/me/partners/${partnerId}/verification-skipped`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cause, status_code: statusCode ?? null }),
  }).catch(() => {
    // Swallowed by design — see above.
  });
}

export interface EsignStatusResponse {
  status: string;
  signer_name?: string;
  /** The envelope-owned address used to deliver the code. */
  intended_email?: string | null;
  /** LOCAL/TEST ONLY: the plaintext one-time code, returned by `POST /code` so a
   *  developer can complete their own identity check without tailing the API
   *  log. The API omits the key entirely outside `development|test`, so treating
   *  it as absent is the correct default everywhere else. */
  dev_code?: string;
  /** From `POST /verify` on success (SESSCRED-1) — the signer session. `verifyEsignCode`'s
   *  own cookie-only comment below predates this: the cookie is cross-site and a browser's
   *  own third-party-cookie policy can silently never store it, so the body is now the
   *  credential a caller should actually carry forward — see `me/esignLink.ts`. */
  session_token?: string;
}

/** Whether this token still addresses an envelope, and what state it is in. */
export function fetchEsignStatus(token: string): Promise<EsignStatusResponse> {
  return publicFetch<EsignStatusResponse>(
    `/esign/${token}`,
    { credentials: "include" },
    ESIGN_API_BASE_URL,
  );
}

/**
 * Send a one-time code to the address bound to the envelope. Never to a client-supplied
 * one.
 *
 * **RESOLVING MEANS A CODE WENT OUT.** A send the server refuses comes back as an error
 * status and `publicFetch` REJECTS with a `PublicApiError` carrying it, so a caller that
 * does nothing at all still fails rather than announcing a code it never sent. The two
 * refusals are `SEND_CODE_THROTTLED_STATUS` and `SEND_CODE_LOCKED_OUT_STATUS`
 * (`@/esign/sendCodeStatus`) — a caller only needs to tell them apart if it puts them on
 * different screens.
 *
 * This is worth stating because it was not always true: the endpoint used to answer a
 * refusal with 200 and the outcome in `status`, which resolved here like any success and
 * left every call site to remember to look in the body. Do not reintroduce a body-read
 * for this; if a new refusal appears, it belongs in the status line.
 */
export function sendEsignCode(token: string): Promise<EsignStatusResponse> {
  return publicFetch<EsignStatusResponse>(
    `/esign/${token}/code`,
    {
      method: "POST",
      credentials: "include",
      // A rejected fetch gets one more chance; any HTTP response is definitive.
      retry: { attempts: 2 },
    },
    ESIGN_API_BASE_URL,
  );
}

/**
 * Submit the code. On success the API sets the `esign_session` cookie AND returns the same
 * token in the body as `session_token` (SESSCRED-1). The cookie is cross-site and a
 * browser's own third-party-cookie policy can silently never store it regardless of
 * `SameSite`/`Secure` — reproduced against a live client on Safari. Callers should store
 * `session_token` (`writeEsignSessionToken`, `me/esignLink.ts`) so the standalone signer
 * app can find it after the S8 hand-off, rather than depending on the cookie surviving.
 */
export function verifyEsignCode(
  token: string,
  code: string,
): Promise<EsignStatusResponse> {
  return publicFetch<EsignStatusResponse>(
    `/esign/${token}/verify`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    },
    ESIGN_API_BASE_URL,
  );
}
